import { Link } from "react-router-dom";
import { Play, ArrowLeft } from "lucide-react";


const FEATURED = {
  id: "pre-symposium-2026",
  youtube_id: "9J-_Lay6a9g",
  title: "Living in Balance: Where Nature and People Thrive Together — Biosphere Reserves in Action",
  description:
    "Explore how Biosphere Reserves across Africa are connecting people, nature and sustainable development while contributing to ecosystem restoration, resilient communities and sustainable livelihoods.",
  tag: "Pre-Symposium Webinar",
};

const SERIES = [
  FEATURED,
  {
    id: "strategic-plan-2025-2030",
    youtube_id: "F4b3xygkamI",
    title: "Launch of the SER Africa Strategic Plan 2025–2030",
    description:
      "Introducing the new strategic direction for the SER Africa Chapter — vision, goals and priorities for ecological restoration across Africa over the next five years.",
    tag: "Strategic Plan",
  },
];

// 7 placeholders for the remaining webinars — admin will fill YouTube IDs in later
const PAST = Array.from({ length: 7 }).map((_, i) => ({
  id: `past-${i + 1}`,
  youtube_id: null,
  title: `SER Africa Webinar ${i + 1}`,
  description: "Recording coming soon — the link will be published here once the video is finalised.",
  tag: "Past Webinar",
}));


function WebinarCard({ w, featured = false }) {
  const thumb = w.youtube_id
    ? `https://img.youtube.com/vi/${w.youtube_id}/maxresdefault.jpg`
    : null;
  const href = w.youtube_id ? `https://youtu.be/${w.youtube_id}` : null;
  const Wrapper = href ? "a" : "div";
  const wrapperProps = href
    ? { href, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...wrapperProps}
      className={`ser-card group overflow-hidden flex flex-col ${featured ? "md:flex-row" : ""}`}
      data-testid={`webinar-card-${w.id}`}
    >
      <div
        className={
          featured
            ? "md:w-2/5 aspect-video md:aspect-auto relative overflow-hidden bg-black"
            : "aspect-video relative overflow-hidden bg-black"
        }
      >
        {thumb ? (
          <img
            src={thumb}
            onError={(ev) => {
              ev.currentTarget.src = `https://img.youtube.com/vi/${w.youtube_id}/hqdefault.jpg`;
            }}
            alt={w.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-xs uppercase tracking-wider"
            style={{ backgroundColor: "var(--ser-section-alt)", color: "var(--ser-text-muted)" }}
          >
            Coming soon
          </div>
        )}
        {href && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
              style={{ backgroundColor: "rgba(255,255,255,0.94)" }}
              aria-hidden
            >
              <Play className="w-6 h-6" style={{ color: "var(--ser-primary-dark)" }} fill="currentColor" />
            </span>
          </div>
        )}
      </div>
      <div className={`p-6 flex flex-col ${featured ? "md:w-3/5 md:p-8" : ""}`}>
        <span
          className="ser-tag self-start mb-3"
          style={
            featured
              ? { backgroundColor: "var(--ser-clay)", color: "white" }
              : {}
          }
        >
          {w.tag}
        </span>
        <h3
          className="mb-3"
          style={{
            color: "var(--ser-teal)",
            fontFamily: "Lato, sans-serif",
            fontWeight: 700,
            fontSize: featured ? "1.1rem" : "0.95rem",
            lineHeight: 1.3,
            letterSpacing: "0.02em",
          }}
        >
          {w.title}
        </h3>
        <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--ser-text-muted)" }}>
          {w.description}
        </p>
        {href && (
          <span
            className="text-xs font-bold uppercase tracking-wider mt-auto"
            style={{ color: "var(--ser-primary)" }}
          >
            Watch on YouTube →
          </span>
        )}
      </div>
    </Wrapper>
  );
}


export default function WebinarsPage() {
  return (
    <div data-testid="webinars-page">
      <section
        className="ser-section"
        style={{
          background: "linear-gradient(160deg, var(--ser-primary-dark) 0%, var(--ser-primary) 70%)",
          color: "white",
        }}
      >
        <div className="ser-container max-w-4xl">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-6"
            style={{ color: "rgba(255,255,255,0.85)" }}
            data-testid="webinars-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Events
          </Link>
          <p className="eyebrow mb-4" style={{ color: "var(--ser-clay)" }}>Video Library</p>
          <h1 className="font-serif-display text-4xl md:text-6xl mb-4">SER Africa Webinars</h1>
          <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.85)" }}>
            Recordings from the SER Africa network — restoration science, policy, practice and
            community stories from across the continent.
          </p>
        </div>
      </section>

      {/* Featured */}
      <section className="ser-section" data-testid="webinars-featured">
        <div className="ser-container max-w-6xl">
          <p className="eyebrow mb-3">Featured</p>
          <h2
            className="font-serif-display text-2xl md:text-3xl uppercase mb-6"
            style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
          >
            Pre-Symposium Webinar
          </h2>
          <WebinarCard w={FEATURED} featured />
        </div>
      </section>

      {/* Webinar Series */}
      <section
        className="ser-section"
        style={{ backgroundColor: "var(--ser-section-alt)" }}
        data-testid="webinars-series"
      >
        <div className="ser-container max-w-6xl">
          <p className="eyebrow mb-3">Signature Programmes</p>
          <h2
            className="font-serif-display text-2xl md:text-3xl uppercase mb-8"
            style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
          >
            Webinar Series
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERIES.map((w) => (
              <WebinarCard key={w.id} w={w} />
            ))}
          </div>
        </div>
      </section>

      {/* Past webinars */}
      <section className="ser-section" data-testid="webinars-past">
        <div className="ser-container max-w-6xl">
          <p className="eyebrow mb-3">Archive</p>
          <h2
            className="font-serif-display text-2xl md:text-3xl uppercase mb-8"
            style={{ color: "var(--ser-teal)", fontWeight: 300, letterSpacing: "0.04em" }}
          >
            Past Webinars &amp; Recordings
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PAST.map((w) => (
              <WebinarCard key={w.id} w={w} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
